import { useState } from 'react';
import { Avatar, Box, IconButton, Stack, TextField, Tooltip } from '@mui/material';
import DriveFileRenameOutlineIcon from '@mui/icons-material/DriveFileRenameOutline';
import DeleteIcon from 'icons/DeleteIcon';
import FileDownloadDoneIcon from '@mui/icons-material/FileDownloadDone';
import CloseIcon from '@mui/icons-material/Close';
import CustomText from 'components/common/CustomText/CustomText';
import CustomDialog from 'components/dialogs/CustomDialog';
import CustomButton from 'components/buttons/CustomButton/CustomButton';
import { colors } from 'styles/colors';
import { useUpdateDiscussionNote, useDeleteDiscussionNote } from 'hooks/useDiscussionNotes';
import { useTimeAgo } from 'hooks/useTimeAgo';
import { snackbarService } from 'services/snackbarService';
import defaultAvatar from 'assets/avatar.png';

interface Props {
  item: any;
  userId: number;
  procedureId: number;
  isOwn: boolean;
  onUpdated: (note: string) => void;
  onDeleted: () => void;
}

export default function DiscussionNoteItem({ item, userId, procedureId, isOwn, onUpdated, onDeleted }: Props) {
  const [isEditing, setIsEditing] = useState(false);
  const [value, setValue] = useState(item.note);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const authorName = item.author ? `${item.author.first_name} ${item.author.last_name}`.trim() : '';
  const timeAgo = useTimeAgo(item.created);

  const { mutate: updateMutation, isLoading: isUpdating } = useUpdateDiscussionNote(
    () => {
      setIsEditing(false);
      onUpdated(value.trim());
    },
    () => {
      snackbarService.showSnackbar('Failed to update note', 'error');
    },
  );

  const { mutate: deleteMutation, isLoading: isDeleting } = useDeleteDiscussionNote(
    () => {
      setConfirmDelete(false);
      onDeleted();
    },
    () => {
      snackbarService.showSnackbar('Failed to delete note', 'error');
    },
  );

  const handleSave = () => {
    const trimmed = value.trim();
    if (!trimmed) return;
    updateMutation({ userId, procedureId, noteId: item.id, note: trimmed });
  };

  return (
    <Box
      p={1.5}
      borderRadius="10px"
      border={`1px solid ${colors.itemBorder}`}
    >
      <Stack direction="row" spacing={1.5} alignItems="flex-start">
        <Avatar
          src={item.author_media?.href_small ?? defaultAvatar}
          alt={authorName}
          sx={{ width: 32, height: 32, bgcolor: colors.avatarBg, fontSize: 14 }}
        />

        <Box flex={1}>
          <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
            <Box>
              <CustomText text={authorName} color={colors.primary} fontWeight={600} fontSize={14} />
              <CustomText text={timeAgo} color={colors.secondary} fontSize={12} />
            </Box>

            {isOwn && !isEditing && (
              <Box display="flex" gap={0.5}>
                <Tooltip title="Edit">
                  <IconButton size="small" onClick={() => { setValue(item.note); setIsEditing(true); }}>
                    <DriveFileRenameOutlineIcon sx={{ fontSize: 16, color: colors.primary }} />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Delete">
                  <IconButton size="small" onClick={() => setConfirmDelete(true)}>
                    <DeleteIcon size={12} color={colors.error} />
                  </IconButton>
                </Tooltip>
              </Box>
            )}
          </Stack>

          {isEditing ? (
            <Stack spacing={1} mt={1}>
              <TextField
                fullWidth
                multiline
                minRows={2}
                size="small"
                value={value}
                onChange={(e) => setValue(e.target.value)}
              />
              <Box display="flex" justifyContent="flex-end" gap={1}>
                <IconButton size="small" onClick={() => setIsEditing(false)}>
                  <CloseIcon sx={{ fontSize: 18, color: colors.secondary }} />
                </IconButton>
                <IconButton size="small" onClick={handleSave} disabled={isUpdating}>
                  <FileDownloadDoneIcon sx={{ fontSize: 18, color: colors.success }} />
                </IconButton>
              </Box>
            </Stack>
          ) : (
            <CustomText text={item.note} color={colors.black} fontSize={14} sx={{ mt: 0.5, whiteSpace: 'pre-wrap' }} />
          )}
        </Box>
      </Stack>

      <CustomDialog
        open={confirmDelete}
        title="Delete Note"
        message="Are you sure you want to delete this note?"
        buttons={[
          <CustomButton
            variant="outlined"
            text="Cancel"
            textColor="error"
            onClick={() => setConfirmDelete(false)}
            disabled={isDeleting}
            height={35}
          />,
          <CustomButton
            variant="filled"
            color="error"
            text="Delete"
            onClick={() => deleteMutation({ userId, procedureId, noteId: item.id })}
            loading={isDeleting}
            height={35}
          />,
        ]}
      />
    </Box>
  );
}
